import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Siren } from "lucide-react";
import { COMPANY } from "./data";
import { SectionHead, Reveal } from "./Reveal";
import { LeadForm } from "./LeadForm";
import { WhatsAppIcon } from "./icons";

const MAPS_QUERY = encodeURIComponent(`${COMPANY.street}, ${COMPANY.city}`);

const InfoRow = ({ icon: Icon, title, children }) => (
  <li className="flex gap-5 border-t border-white/10 py-5">
    <Icon size={20} className="mt-1 shrink-0 text-terra" />
    <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{title}</p><div className="mt-1 text-base text-white">{children}</div></div>
  </li>
);

const MapEmbed = ({ consent }) => {
  const [load, setLoad] = useState(false);
  const show = load || consent === "all";
  return (
    <div className="relative mt-16 h-[340px] overflow-hidden border border-white/10 bg-ink2 sm:h-[440px]" data-testid="map-embed">
      {show ? (
        <iframe title="Standort Christian Bedachungen, Hauptstr. 3, Schifferstadt" src={`https://maps.google.com/maps?q=${MAPS_QUERY}&z=15&output=embed`}
          className="map-dark h-full w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="map-iframe" />
      ) : (
        <div className="tile-pattern flex h-full flex-col items-center justify-center p-6 text-center">
          <MapPin size={36} className="text-terra" />
          <p className="mt-4 font-display text-2xl font-bold uppercase text-white">{COMPANY.street} · {COMPANY.city}</p>
          <p className="mt-3 max-w-md text-sm text-slate-300">Mit dem Laden der Karte werden Daten an Google übertragen. Es gelten die Datenschutzbestimmungen von Google.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={() => setLoad(true)} className="btn-primary !py-3" data-testid="map-load-btn">Karte laden</button>
            <a href={`https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-3" data-testid="map-open-link">In Google Maps öffnen</a>
          </div>
        </div>
      )}
    </div>
  );
};

export const Contact = ({ consent }) => (
  <section id="kontakt" aria-labelledby="kontakt-title" className="relative bg-ink py-24 sm:py-32 lg:py-40" data-testid="contact-section">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHead id="kontakt-title" eyebrow="Kontakt" title={<>Kostenloses<br />Angebot.</>} intro="Vier Angaben, ein Klick – wir rufen Sie innerhalb von 24 Stunden zurück und besichtigen Ihr Dach innerhalb von 48 Stunden. Kostenlos." />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col gap-3">
              <a href={COMPANY.phoneHref} className="btn-primary btn-mega justify-center" data-testid="contact-phone-link"><Phone size={20} /> Anrufen: {COMPANY.phoneDisplay}</a>
              <div className="grid grid-cols-2 gap-3">
                <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="btn-ghost justify-center !px-3" data-testid="contact-whatsapp-link"><WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> WhatsApp</a>
                <a href={`mailto:${COMPANY.email}`} className="btn-ghost justify-center !px-3" data-testid="contact-email-btn"><Mail size={18} /> E-Mail</a>
              </div>
            </div>
            <ul className="mt-10">
              <InfoRow icon={Mail} title="E-Mail"><a href={`mailto:${COMPANY.email}`} className="break-all hover:text-terra" data-testid="contact-email-link">{COMPANY.email}</a></InfoRow>
              <InfoRow icon={MapPin} title="Adresse"><address className="not-italic">{COMPANY.full}<br />{COMPANY.street}, {COMPANY.city}</address></InfoRow>
              <InfoRow icon={Clock} title="Öffnungszeiten">{COMPANY.hours}</InfoRow>
              <InfoRow icon={Siren} title="Sturmschaden-Notdienst"><span className="font-semibold">Sturmschaden? Innerhalb von 24 h vor Ort.</span></InfoRow>
            </ul>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div id="angebot" className="border border-white/10 bg-ink2 p-6 sm:p-10 lg:p-12">
            <LeadForm />
          </div>
        </Reveal>
      </div>
      <MapEmbed consent={consent} />
    </div>
  </section>
);
